import type { FC } from 'react';
import { css, Global } from '@emotion/react';
import { mq, Theme } from '../Theme';

type CSSProps = {
  theme: Theme;
};

/** Global CSS, like `@font-face` rules, CSS resets, etc. */
const CSS: FC<CSSProps> = ({ theme }) => (
  <Global
    styles={css`
      @font-face {
        src:
          local(''),
          url('/imgs/fonts/quicksand-bold.woff2') format('woff2'),
          url('/imgs/fonts/quicksand-bold.woff') format('woff');
        font-family: Quicksand;
        font-weight: 700;
        font-style: normal;
        font-display: swap;
      }

      @font-face {
        src:
          local(''),
          url('/imgs/fonts/sourcesanspro-regular.woff2') format('woff2'),
          url('/imgs/fonts/sourcesanspro-regular.woff') format('woff');
        font-family: 'Source Sans Pro';
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      }

      @font-face {
        src:
          local(''),
          url('/imgs/fonts/sourcesanspro-semibold.woff2') format('woff2'),
          url('/imgs/fonts/sourcesanspro-semibold.woff') format('woff');
        font-family: 'Source Sans Pro';
        font-weight: 600;
        font-style: normal;
        font-display: swap;
      }

      * {
        box-sizing: border-box;
      }

      html {
        color-scheme: light;
        font-family: ${theme.fontFamily.default};
        text-size-adjust: none;
        interpolate-size: allow-keywords;
      }

      /**
       * Apply smooth scrolling only if the user has not set a preference for reduced motion.
       * This is a best practice for accessibility, as some users may experience vertigo with smooth scrolling.
       * The computed value of this property is also used by document.scrollIntoView().
       */
      @media (prefers-reduced-motion: no-preference) {
        html {
          scroll-behavior: smooth;
        }
      }

      body {
        margin: 0;
        font-size: ${theme.fontSize.xs.size};
        line-height: 1.25;

        /* Grayscale smoothing: on macOS, the default subpixel smoothing makes
           the text look heavier than in the design. */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;

        & > div {
          overflow-x: hidden;

          &#storybook-root {
            overflow-x: unset;
          }
        }
      }

      p {
        margin: 0;
      }

      ul {
        margin: 0;
        padding: 0;
        list-style-type: none;
      }

      table {
        border-collapse: collapse;
        border-spacing: 0;
      }
    `}
  />
);

const layoutContentOnlyPadding = css`
  padding-left: 1rem;
  padding-right: 1rem;

  ${mq('sm')} {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  ${mq('xl')} {
    padding-left: clamp(2rem, calc((100vw - 1220px) / 2), 22rem);
    padding-right: clamp(2rem, calc((100vw - 1220px) / 2), 22rem);
  }

  ${mq('xxl')} {
    padding-left: clamp(2rem, 350px, 22rem);
    padding-right: clamp(2rem, 350px, 22rem);
  }
`;

const layoutContentOnlyMargin = css`
  margin-left: 1rem;
  margin-right: 1rem;

  ${mq('sm')} {
    margin-left: 2rem;
    margin-right: 2rem;
  }

  ${mq('xl')} {
    margin-left: clamp(2rem, calc((100vw - 1220px) / 2), 22rem);
    margin-right: clamp(2rem, calc((100vw - 1220px) / 2), 22rem);
  }

  ${mq('xxl')} {
    margin-left: clamp(2rem, 350px, 22rem);
    margin-right: clamp(2rem, 350px, 22rem);
  }
`;

const layoutContainerContent = css`
  ${layoutContentOnlyPadding};

  margin-left: -1rem;
  margin-right: -1rem;

  ${mq('sm')} {
    margin-left: -2rem;
    margin-right: -2rem;
  }

  ${mq('xl')} {
    margin-left: calc(max(2rem, min(calc((100vw - 1220px) / 2), 22rem)) * -1);
    margin-right: calc(max(2rem, min(calc((100vw - 1220px) / 2), 22rem)) * -1);
  }

  ${mq('xxl')} {
    margin-left: calc(max(2rem, min(350px, 22rem)) * -1);
    margin-right: calc(max(2rem, min(350px, 22rem)) * -1);
  }
`;

export {
  CSS,
  layoutContainerContent,
  layoutContentOnlyPadding,
  layoutContentOnlyMargin,
};
